/** Auth mutations. Thin wrappers so screens stay declarative. */

import { useMutation } from '@tanstack/react-query'

import { api } from '../api/client'
import { auth } from '../supabase/client'

export interface Credentials {
  email: string
  password: string
}

export function useSignIn() {
  return useMutation({
    mutationFn: async ({ email, password }: Credentials) => {
      const { data, error } = await auth.signInWithPassword({ email, password })
      if (error) throw new Error(friendlyAuthMessage(error.message))
      return data
    },
  })
}

export function useSignUp() {
  return useMutation({
    mutationFn: async ({
      email,
      password,
      fullName,
      reseller,
    }: Credentials & {
      fullName: string
      /** Set when the person signs up to become a reseller (needs approval). */
      reseller?: { businessName: string; phone: string }
    }) => {
      // A reseller needs no email step: the server creates the confirmed login and
      // the pending application together, then we sign straight in. The panel
      // stays closed until a Super Admin approves.
      if (reseller) {
        await api.post('/reseller/v1/signup', {
          email,
          password,
          full_name: fullName,
          business_name: reseller.businessName,
          ...(reseller.phone ? { phone: reseller.phone } : {}),
        })
        const { data, error } = await auth.signInWithPassword({ email, password })
        if (error) throw new Error(friendlyAuthMessage(error.message))
        return data
      }
      const { data, error } = await auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      })
      if (error) throw new Error(friendlyAuthMessage(error.message))
      return data
    },
  })
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (email: string) => {
      const { error } = await auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset`,
      })
      if (error) throw new Error(friendlyAuthMessage(error.message))
    },
  })
}

export function useSignInWithOAuth() {
  return useMutation({
    mutationFn: async (provider: 'google' | 'azure') => {
      const { error } = await auth.signInWithOAuth({
        provider,
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      })
      if (error) throw new Error(friendlyAuthMessage(error.message))
    },
  })
}

/**
 * Supabase messages are written for developers. These are written for users.
 *
 * Note the deliberate vagueness on bad credentials: distinguishing "no such
 * account" from "wrong password" tells an attacker which emails are registered.
 */
function friendlyAuthMessage(raw: string): string {
  const message = raw.toLowerCase()
  if (message.includes('invalid login credentials')) {
    return 'That email and password don’t match. Please try again.'
  }
  if (message.includes('email not confirmed')) {
    return 'Please confirm your email first — check your inbox for the link.'
  }
  if (message.includes('already registered') || message.includes('already been registered')) {
    return 'An account with that email already exists. Try signing in instead.'
  }
  if (message.includes('rate limit') || message.includes('too many')) {
    return 'Too many attempts. Please wait a minute and try again.'
  }
  if (message.includes('password') && message.includes('should be at least')) {
    return 'Please choose a password of at least 12 characters.'
  }
  return raw
}
